import { of } from 'rxjs';
import { getPortal } from '../../common/constants/pathVariables.js';
import { getLocale } from '../../common/i18n/index.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import { getMdmIdFromUrlParams } from '../../common/utils/urlUtils';
import { AuthenticatedUserRole } from '../../generated/api/models.js';
import { getActiveAccountMasterId, getPrimaryMdmId } from '../common/localStorageUtils.js';
import { ActionPhase } from '../common/storeUtils.js';
import type { ErrorAction, ErrorActionCreator, TypeKeys } from '../actions/index.js';
import type { ActionState, ActionWithId, ActionsHistory, ItemsQuery, State } from '../common/store.js';
import type { StateObservable } from 'redux-observable';
import type { Observable } from 'rxjs';
import type { AjaxError } from 'rxjs/ajax';

/**
 * Separate http calls to dependencies so they can be injected manually when testing.
 */
export interface EpicDependencies {
  get<T>(url: string, headers?: Record<string, string>): Observable<T>;
  getJSON<T>(url: string, headers?: Record<string, string>): Observable<T>;
  post<T>(url: string, body?: object, headers?: Record<string, string>): Observable<T>;
  put<T>(url: string, body?: object, headers?: Record<string, string>): Observable<T>;
  patch<T>(url: string, body?: object, headers?: Record<string, string>): Observable<T>;
  delete<T>(url: string, body?: object, headers?: Record<string, string>): Observable<T>;
}

export interface ActionAndState {
  action: ActionWithId;
  state?: ActionState;
}

const getFromState = (state$: StateObservable<State>, key: string): object | undefined => {
  // @ts-ignore
  if (state$ && state$.value.selfservice && state$.value.selfservice[key]) {
    // @ts-ignore
    return state$.value.selfservice[key];
    // @ts-ignore
  } else if (state$ && state$.value.user && state$.value.user[key]) {
    // @ts-ignore
    return state$.value.user[key];
    // @ts-ignore
  } else if (state$ && state$.value.authentication && state$.value.authentication[key]) {
    // @ts-ignore
    return state$.value.authentication[key];
    // @ts-ignore
  } else if (state$ && state$.value[key]) {
    // @ts-ignore
    return state$.value[key];
  }
  return undefined;
};

export const getActionState = (
  action: ActionWithId,
  state: ActionsHistory | undefined | null
): ActionState | undefined => {
  if (state && state.actions) {
    return state.actions.find((previousActionState: ActionState) => previousActionState.value.id === action.id);
  }
  return undefined;
};

// TODO: this can be generic, just requires some refactoring. Then we can get rid off some casting stuff in epics.
// function actionToActionState<T extends ActionWithId>(action: T, state$: StateObservable<State>, key: string): ActionAndState<T> {
export function actionToActionState(action: ActionWithId, state$: StateObservable<State>, key: string): ActionAndState {
  const selfserviceState = getFromState(state$, key);
  let actionState: ActionState | undefined;
  if (selfserviceState) {
    actionState = getActionState(action, selfserviceState as ActionsHistory);
  }
  return { action: action, state: actionState };
}

export const getActionStateFromMultiple = (
  action: ActionWithId,
  category: string,
  states: { [s: string]: ActionsHistory | undefined | null }
) => {
  // eslint-disable-next-line no-prototype-builtins
  if (states && states.hasOwnProperty(category)) {
    const actionState = getActionState(action, states[category]);
    if (actionState) {
      return actionState;
    }
  }
  return undefined;
};

export function actionToActionStateFromMultiple(
  action: ActionWithId,
  state$: StateObservable<State>,
  key: string,
  category: string
): ActionAndState {
  const selfserviceState = getFromState(state$, key);
  let actionState: ActionState | undefined;
  if (selfserviceState) {
    actionState = getActionStateFromMultiple(
      action,
      category,
      selfserviceState as {
        [s: string]: ActionsHistory;
      }
    );
  }
  return { action: action, state: actionState };
}

export function requireStateInProgress(actionAndState: ActionAndState) {
  return actionAndState.state !== undefined && actionAndState.state.phase === ActionPhase.IN_PROGRESS;
}

export const getOnboardingId = () => (isInBrowser() ? sessionStorage.getItem('onboardingId') : undefined);

export const clearOnboardingId = () => {
  if (isInBrowser()) {
    sessionStorage.removeItem('onboardingId');
  }
};

export const getAccountMasterIdHeader = (): Record<string, string> | undefined => {
  const accountMasterId = getActiveAccountMasterId() || getPrimaryMdmId();

  if (accountMasterId) {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-API-Account-Master-ID': accountMasterId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-Elisa-Company-MDM-ID': accountMasterId,
    };
  }

  return undefined;
};

export const getMdmIdHeaderFromUrlParams = (): Record<string, string> | undefined => {
  const mdmId = getMdmIdFromUrlParams();
  return mdmId
    ? {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-API-Account-Master-ID': mdmId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Elisa-Company-MDM-ID': mdmId,
      }
    : undefined;
};

export const getPrimaryMdmIdHeader = (): Record<string, string> | undefined => {
  const mdmId = getPrimaryMdmId();
  return mdmId
    ? {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-API-Account-Master-ID': mdmId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Elisa-Company-MDM-ID': mdmId,
      }
    : undefined;
};

export function getError<T extends TypeKeys>(
  error: AjaxError,
  errorActionCreator: ErrorActionCreator<T>,
  params?: { [s: string]: string }
): Observable<ErrorAction<T>> {
  return of(errorActionCreator(error.message, error.status, error.response, params));
}

export const itemsQueryToQueryParams = ({ offset, limit }: ItemsQuery): { [s: string]: string | number } => {
  const queryParams: { [s: string]: string | number } = {};
  queryParams.offset = offset !== undefined ? offset : 0;
  if (limit) {
    queryParams.limit = limit;
  }
  return queryParams;
};

export const UI_API_PRIVATE_BASE_PATH = '/api/ui/v2/private';

// This method determines the override user role. For example if a user has access to both NOE and EOE,
// then role in Salesforce is KEY_USER (Admin profile). When such a user accesses EOE, then override
// role should be EMPLOYEE so that user gets only employee relevant stuff from APIs
export const getUserRoleHeader = (): Record<string, string> => {
  const portal = getPortal(window.location.pathname);
  if (portal === 'EOE' || portal === 'AuxEsim') {
    return { ['X-API-User-Role']: AuthenticatedUserRole.EMPLOYEE };
  } else if (portal === 'Punchout') {
    return { ['X-API-User-Role']: AuthenticatedUserRole.PUNCHOUT_USER };
  } else {
    return {};
  }
};

export const getLocaleHeader = (): { [s: string]: string } => {
  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Accept-Language': getLocale(),
  };
};
